import type { OmitStrict } from '@pega/cosmos-react-core';
import type { IconTileProps } from '@pega/cosmos-react-build';

export type RuleType =
  | 'Automation'
  | 'CustomFunction'
  | 'Decision'
  | 'Function'
  | 'PredictiveModel'
  | 'Validation'
  | 'When'
  | 'ConfigurationSet'
  | 'ConfigurationSettingValue'
  | 'ConfigurationSetting'
  | 'Correspondence'
  | 'Scheduler'
  | 'DataConnection'
  | 'DataPage'
  | 'DataRecord'
  | 'Field'
  | 'Insight'
  | 'Query'
  | 'EmailAccount'
  | 'IntegrationSystem'
  | 'JSONTransform'
  | 'RESTConnector'
  | 'Action'
  | 'CaseStatus'
  | 'CaseType'
  | 'Process'
  | 'ServiceLevelAgreement'
  | 'Stage'
  | 'WorkQueue'
  | 'Notification'
  | 'AccessControlPolicyCondition'
  | 'AccessGroup'
  | 'AccessControlPolicy'
  | 'AccessRole'
  | 'AuthenticationProfile'
  | 'Class'
  | 'SearchModelDefinition'
  | 'DocumentTemplate'
  | 'Portal'
  | 'Theme'
  | 'View';

type IconTileConfig = Record<RuleType, OmitStrict<IconTileProps, 'size'>>;

export const mockIconTileData: IconTileConfig = {
  Automation: {
    name: 'gear-play',
    category: 'automation',
    label: 'Automation object'
  },
  CustomFunction: {
    name: 'function',
    category: 'logic',
    label: 'Function logic',
    inverted: true
  },
  Decision: {
    name: 'diamond',
    category: 'logic',
    label: 'Decision rule logic',
    inverted: true
  },
  Function: {
    name: 'function',
    category: 'logic',
    label: 'Function logic',
    inverted: true
  },
  PredictiveModel: {
    name: 'polaris-solid',
    category: 'ai',
    label: 'Ai object'
  },
  Validation: {
    name: 'check-badge',
    category: 'logic',
    label: 'Validation logic',
    inverted: true
  },
  When: {
    name: 'shuffle',
    category: 'logic',
    label: 'When logic',
    inverted: true
  },
  ConfigurationSet: {
    name: 'toolbox-open',
    category: 'rule',
    label: 'Configuration rule',
    inverted: true
  },
  ConfigurationSettingValue: {
    name: 'toolbox-open',
    category: 'rule',
    label: 'Configuration rule',
    inverted: true
  },
  ConfigurationSetting: {
    name: 'toolbox-open',
    category: 'rule',
    label: 'Configuration rule',
    inverted: true
  },
  Correspondence: {
    name: 'toolbox-open',
    category: 'rule',
    label: 'Configuration rule',
    inverted: true
  },
  Scheduler: {
    name: 'toolbox-open',
    category: 'rule',
    label: 'Configuration rule',
    inverted: true
  },
  DataConnection: {
    name: 'plug',
    category: 'rule',
    label: 'Connector rule',
    inverted: true
  },
  DataPage: {
    name: 'database-search',
    category: 'rule',
    label: 'Data page rule',
    inverted: true
  },
  DataRecord: {
    name: 'dataviz-table-with-bars',
    category: 'rule',
    label: 'Record rule',
    inverted: true
  },
  Field: {
    name: 'field-cursor',
    category: 'rule',
    label: 'Field rule',
    inverted: true
  },
  Insight: {
    name: 'search-analytics',
    category: 'rule',
    label: 'Insight rule',
    inverted: true
  },
  Query: {
    name: 'database-search',
    category: 'rule',
    label: 'Data page rule',
    inverted: true
  },
  EmailAccount: {
    name: 'plug',
    category: 'rule',
    label: 'Connector rule',
    inverted: true
  },
  IntegrationSystem: {
    name: 'disc-stack',
    category: 'rule',
    label: 'Integration system rule',
    inverted: true
  },
  JSONTransform: {
    name: 'plug',
    category: 'rule',
    label: 'Connector rule',
    inverted: true
  },
  RESTConnector: {
    name: 'plug',
    category: 'rule',
    label: 'Connector rule',
    inverted: true
  },
  Action: {
    name: 'user-pencil',
    category: 'task',
    label: 'Action task',
    inverted: true
  },
  CaseStatus: {
    name: 'chevron',
    category: 'case',
    label: 'Case stage',
    inverted: true
  },
  CaseType: {
    name: 'case',
    category: 'case',
    label: 'Case object'
  },
  Process: {
    name: 'flow-process',
    category: 'case',
    label: 'Case process',
    inverted: true
  },
  ServiceLevelAgreement: {
    name: 'clock',
    category: 'case',
    label: 'Case SLA',
    inverted: true
  },
  Stage: {
    name: 'chevron',
    category: 'case',
    label: 'Case stage',
    inverted: true
  },
  Notification: {
    name: 'bell-push-notification',
    category: 'case',
    label: 'Notification',
    inverted: true
  },
  WorkQueue: {
    name: 'chevron',
    category: 'case',
    label: 'Case stage',
    inverted: true
  },
  AccessControlPolicyCondition: {
    name: 'shield',
    category: 'rule',
    label: 'Permission rule',
    inverted: true
  },
  AccessGroup: {
    name: 'shield',
    category: 'rule',
    label: 'Permission rule',
    inverted: true
  },
  AccessControlPolicy: {
    name: 'shield',
    category: 'rule',
    label: 'Permission rule',
    inverted: true
  },
  AccessRole: {
    name: 'shield',
    category: 'rule',
    label: 'Permission rule',
    inverted: true
  },
  AuthenticationProfile: {
    name: 'shield',
    category: 'rule',
    label: 'Permission rule',
    inverted: true
  },
  Class: {
    name: 'code',
    category: 'rule',
    label: 'System rule',
    inverted: true
  },
  SearchModelDefinition: {
    name: 'code',
    category: 'rule',
    label: 'System rule',
    inverted: true
  },
  DocumentTemplate: {
    name: 'multi-device',
    category: 'rule',
    label: 'Portal rule',
    inverted: true
  },
  Portal: {
    name: 'multi-device',
    category: 'rule',
    label: 'Portal rule',
    inverted: true
  },
  Theme: {
    name: 'multi-device',
    category: 'rule',
    label: 'Portal rule',
    inverted: true
  },
  View: {
    name: 'newspaper',
    category: 'rule',
    label: 'View rule',
    inverted: true
  }
};

export const getIconConfig = (type: RuleType): OmitStrict<IconTileProps, 'size'> =>
  mockIconTileData[type];
